/* eslint-disable */
import React, { useState, useEffect, SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason } from '@mui/material/Autocomplete';
import Icon from '@mui/material/Icon';
import Box from '@mui/material/Box';

// MUI Icons
import LockIcon from '@mui/icons-material/Lock';

// Custom Components
import fieldFormat from '@/lib/fieldFormat';

// Types
import { IPickerControlProps, PickerItem } from '@/@types/controls/controls';
import { IFieldPickerModel, IPickerItems, PickerItemValue } from '@/@types/models/model';

const PickerControl = (props: IPickerControlProps) => {
  const [inputValue, setInputValue] = useState('');
  const [items, setItems] = useState<Array<PickerItem>>([]);
  const [displayedItemsCount, setDisplayedItemsCount] = useState(100); // Initial items to display

  const [resolvedValue, setResolvedValue] = useState<PickerItem | PickerItem[] | null>(null);

  const { t } = useTranslation();

  const { field, value, onChange, validation, formMode, controlMode, dc, listboxMaxHeight } = props;

  const offset = 100; // Items to load each time

  useEffect(() => {
    if (!items || items.length === 0) return;

    const findItem = (val: PickerItemValue): PickerItem | undefined => items.find((item) => item.value === val);

    if (Array.isArray(value)) {
      const resolved = value.map((val) => (typeof val === 'object' ? val : findItem(val))).filter(Boolean) as PickerItem[];
      setResolvedValue(resolved);
    } else if (value) {
      const resolved = typeof value === 'object' ? value : findItem(value);
      if (resolved) setResolvedValue(resolved);
    } else {
      setResolvedValue(isMulti ? [] : null);
    }
  }, [items, value]);

  useEffect(() => {
    refreshItems();
  }, [dc]);

  // Reset displayed items count when input value changes (search)
  useEffect(() => {
    setDisplayedItemsCount(100);
  }, [inputValue]);

  const handleChange = (
    evt: SyntheticEvent,
    value: PickerItem | PickerItem[] | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<PickerItem> | undefined
  ) => {
    switch (reason) {
      case 'createOption':
        break;
      case 'selectOption':
        setInputValue('');
        applyChange(value);
        break;
      case 'removeOption':
        setInputValue('');
        if (Array.isArray(value) && value.length === 0) {
          applyChange(null);
        } else {
          applyChange(value);
        }
        break;
      case 'clear':
        setInputValue('');
        applyChange(null);
        break;
      case 'blur':
        break;
      default:
        break;
    }
  };

  const handleInputChange = (event: SyntheticEvent, value: string) => {
    setInputValue(value);
  };

  const applyChange = (option: PickerItem | PickerItem[] | null) => {
    if (onChange) {
      onChange(option, field.source);
    }
  };

  // Filter items based on search input with null safety
  const filteredItems = useMemo(() => {
    if (!inputValue || resolvedValue) {
      return items;
    }

    const searchTerm = inputValue.toLowerCase();
    return items.filter((item) => {
      // Handle null/undefined labels safely
      const label = item?.label ?? '';
      return String(label).toLowerCase().includes(searchTerm);
    });
  }, [items, inputValue]);

  // Limit displayed items for performance (lazy loading)
  const displayItems = useMemo(() => {
    return filteredItems.slice(0, displayedItemsCount);
  }, [filteredItems, displayedItemsCount]);

  const loadMoreItems = () => {
    if (displayedItemsCount < filteredItems.length) {
      setDisplayedItemsCount((prev) => Math.min(prev + offset, filteredItems.length));
    }
  };

  const refreshItems = () => {
    if (field && Object.prototype.hasOwnProperty.call(field, 'subModel') && dc) {
      dc.GetData()
        .then((data) => {
          if (Array.isArray(data) && data.length > 0) {
            const dcItems: Array<PickerItem> = data.filter((x) => x !== null) as PickerItem[];
            setItems(dcItems);
          } else if (field.items) {
            setItems(parsePickerItemFieldDefinition(field.items));
          } else {
            setItems([]);
          }
        })
        .catch(() => {
          if (field.items) {
            setItems(parsePickerItemFieldDefinition(field.items));
          } else {
            setItems([]);
          }
        });
    } else if (field.items) {
      setItems(parsePickerItemFieldDefinition(field.items));
    } else {
      setItems([]);
    }
  };

  const parsePickerItemFieldDefinition = (fieldItems: IPickerItems): PickerItem[] => {
    const { labels, values, icons = [], iconColors = [], default: defaultValue, disabled = [] } = fieldItems;

    return values.map((value, index) => ({
      label: labels[index] ? (field.translate ? t(labels[index]) : labels[index]) : String(value),
      value,
      icon: icons[index] ?? undefined,
      color: iconColors[index] ?? undefined,
      disabled: disabled.includes(value),
    }));
  };

  const isRequired = !!(field && field.validation && field.validation.required);

  const hasError = validation && validation.valid === false;

  const loadingText = t('core:common.loading');
  const noOptionsText = t('core:common.no_options');
  const isReadOnly = controlMode === 'view' || (field && field.readonly);
  const isLoading = !(
    dc ||
    (field && field.items && field.items.values) ||
    (field && Object.prototype.hasOwnProperty.call(field, 'subModel') && (field as IFieldPickerModel).external)
  );
  const hideLabel = formMode === 'table';
  const label = t(field.ttoken);
  const placeholder = field && field.placeholder && field.placeholder.length ? field.placeholder : '';

  const isMulti = !!(field && field.multi);

  let displayValue = '';
  if (isMulti && Array.isArray(resolvedValue)) {
    displayValue = resolvedValue.map((item) => item.label).join('; ');
  } else if (!isMulti && resolvedValue && !Array.isArray(resolvedValue)) {
    displayValue = resolvedValue.label;
  }

  const comparePickerItems = (option: PickerItem, value: PickerItem) => {
    return option.value == value.value;
  };

  const getItemLabel = (item: PickerItem) => (field.translate ? t(item.label) : item.label);

  const renderPickerItemContent = (item: PickerItem) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {item.icon ? (
        <Icon
          sx={{
            color: item.color || undefined,
          }}
          color={item.color ? undefined : 'primary'}
          fontSize="small"
        >
          {item.icon}
        </Icon>
      ) : null}
      <span>{getItemLabel(item)}</span>
    </Box>
  );

  // Custom Listbox component for infinite scroll
  const ListboxComponent = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>((props, ref) => {
    const { children, ...other } = props;

    const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
      const listboxNode = event.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = listboxNode;

      // Load more items when near the bottom
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        loadMoreItems();
      }
    };

    return (
      <ul
        {...other}
        ref={ref}
        onScroll={handleScroll}
        style={{
          ...other.style,
          maxHeight: listboxMaxHeight || '250px',
          overflow: 'auto',
        }}
      >
        {children}
      </ul>
    );
  });

  return isReadOnly ? (
    <TextField
      margin="none"
      fullWidth
      error={hasError}
      label={hideLabel ? null : label}
      placeholder={placeholder ? `${t(placeholder)}` : ''}
      id={field.source}
      value={isMulti ? displayValue : resolvedValue ? (field.translate ? t((resolvedValue as PickerItem).label) : (resolvedValue as PickerItem).label) : ''}
      InputProps={{
        readOnly: isReadOnly,
        startAdornment:
          !isMulti &&
          resolvedValue &&
          !Array.isArray(resolvedValue) &&
          (resolvedValue as PickerItem).icon ? (
            <Icon
              sx={{
                color: (resolvedValue as PickerItem).color || undefined,
                mr: 1,
              }}
              color={(resolvedValue as PickerItem).color ? undefined : 'primary'}
              fontSize="small"
            >
              {(resolvedValue as PickerItem).icon}
            </Icon>
          ) : null,
        endAdornment: isReadOnly ? <LockIcon style={{ fontSize: 20 }} color="disabled" /> : null,
      }}
      helperText={hasError ? t(validation.msg) : field.tooltip ? t(field.tooltip) : ''}
      variant="outlined"
      color="primary"
    />
  ) : (
    <Autocomplete
      disableCloseOnSelect={isMulti}
      disablePortal
      options={displayItems}
      getOptionDisabled={(option) => !!option.disabled}
      value={!resolvedValue && isMulti ? [] : resolvedValue}
      multiple={isMulti}
      getOptionLabel={(option: PickerItem) => getItemLabel(option)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      ListboxComponent={ListboxComponent}
      renderOption={(optionProps, option) => (
        <li {...optionProps}>
          {renderPickerItemContent(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          required={isRequired}
          error={hasError}
          label={t(label)}
          helperText={hasError ? t(validation.msg) : field.tooltip ? t(field.tooltip) : ''}
        />
      )}
      onChange={handleChange}
      loading={isLoading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      isOptionEqualToValue={comparePickerItems}
      filterOptions={(options) => options} // We handle filtering ourselves
    />
  );
};

export default PickerControl;
